// apps/web/src/routes/$lang/_layout/stays.checkout.tsx
import { createFileRoute } from "@tanstack/react-router";
import { StayCheckoutPage } from "../../../features/stays/pages/stay-checkout-page";

export interface StaysCheckoutSearch {
  slug?: string;
  /** The exact offer picked on the property page. Resolved server side. */
  offerId?: string;
  /** Fallback identifier for when the offer has aged out. */
  rateKey?: string;
  /** Comma separated child ages, mirroring the property page. */
  childAges?: string;
  checkin?: string;
  checkout?: string;
  adults?: number;
}

/**
 * Carries the rate the guest picked on the property page (RoomRow's Select
 * link) through to checkout: `slug` + `rateKey` identify the rate, and
 * `checkin`/`checkout`/`adults` are re-sent to `useStayProperty` so the page
 * re-reads the room's current price and cancellation terms.
 *
 * `rateKey`, never `offerId`. The supplier mints a fresh offerId on every
 * search, so an offerId carried across this navigation matched nothing on
 * arrival and every booking died on "we could not find this room". rateKey is
 * stable across searches, and checkout books with the offerId from the search
 * it just made itself. See stayRateKey in packages/shared/src/schemas/stays.ts.
 */
export const Route = createFileRoute("/$lang/_layout/stays/checkout")({
  validateSearch: (search: Record<string, unknown>): StaysCheckoutSearch => {
    const slug = typeof search.slug === "string" ? search.slug : undefined;
    const offerId = typeof search.offerId === "string" ? search.offerId : undefined;
    const rateKey = typeof search.rateKey === "string" ? search.rateKey : undefined;
    const childAges = typeof search.childAges === "string" ? search.childAges : undefined;
    const checkin = typeof search.checkin === "string" ? search.checkin : undefined;
    const checkout = typeof search.checkout === "string" ? search.checkout : undefined;
    const adultsRaw = search.adults;
    const adults =
      typeof adultsRaw === "number"
        ? adultsRaw
        : typeof adultsRaw === "string" && adultsRaw.trim() !== ""
          ? Number(adultsRaw)
          : undefined;
    return {
      ...(slug ? { slug } : {}),
      ...(offerId ? { offerId } : {}),
      ...(rateKey ? { rateKey } : {}),
      ...(childAges ? { childAges } : {}),
      ...(checkin ? { checkin } : {}),
      ...(checkout ? { checkout } : {}),
      ...(adults !== undefined && Number.isFinite(adults) ? { adults } : {}),
    };
  },
  component: StayCheckoutPage,
});
